// Does the person in front of this raisedbeef.com page get to see it?
//
// Reads two things and hands them to decideAccess() in src/lib/siteAccess.ts,
// which holds the rules and the tests. Nothing here decides anything.
//
//   · the session — are they signed in at all
//   · rb_site_my_billing — their level ('admin' | 'premium' | 'trial' |
//     'none', from the database's rb_site_level) and the 30-day clock
//
// ⚠️ ONE SOURCE FOR THE PLAN: rb_site_level, the same function the account
// page and (step 3) the locked index readers ask. The page and the database
// cannot disagree about who is on what.
//
// ⚠️ NO TERMINAL TIER (owner, 2026-10-12): a Terminal subscription no longer
// opens raisedbeef.com. "On RaisedBeef.com must be completely separate."
//
// ⚠️ SELF-CONTAINED ON PURPOSE — plain state and effects, no React Query and
// no UserProfile context — because the gate also wraps pages that boot from
// their own entry (the barn top-prices pages, entry-rbbarnlb.tsx), which mount
// neither provider.

import { useEffect, useState } from "react";

import { supabase } from "@/integrations/supabase/client";
import { decideAccess, type Access, type Level } from "@/lib/siteAccess";
import { deviceId } from "@/lib/throwawayEmail";

/** ONE PLAN (2026-10-25): a plan is either held or not. The database row may
 *  still say 'basic' for the one grandfathered subscription; here it is
 *  simply "the plan". */
type Plan = "premium" | null;

interface Read {
  level: Level | null;
  trialEndsAt: string | null;
  name: string | null;
  /** The paid plan while it is live, and a free plan while it lasts. */
  paid: Plan;
  free: Plan;
  freeUntil: string | null;
  app: AppPlan | null;
}

/** A plan bought in the app (Apple / Google), while it is live — rb_app_store_subscriptions. */
export interface AppPlan {
  store: string | null;
  expiresAt: string | null;
  trial: boolean;
  willRenew: boolean;
}

/** What the person is on, in words a screen can use (the app's You tab). */
export interface SitePlan {
  level: Level | null;
  paid: Plan;
  free: Plan;
  freeUntil: string | null;
  /** Bought in the app. Kept apart from `paid` (Stripe): it is changed in the phone's Settings. */
  app?: AppPlan | null;
}

type Session = { id: string; email: string | null } | null;

/** The last answer this tab got, so the next gated page opens on it.
 *
 * ⚠️ WHY (owner, 2026-10-13: the leaderboard "flickers"). Every gated page
 * started from "loading": an empty shell, then the page drawn over it — the
 * whole masthead and index strip torn down and rebuilt on every menu tap. The
 * page now opens on the last answer and the reads below still run, so a
 * sign-out or a plan change still lands; it just does not blink first. */
let lastSession: Session | undefined;
let lastRead: { userId: string; read: Read } | null = null;

/** Accounts this page load has already noted (rb_site_note_device), so the
 *  note is sent once per person per load, not on every gated page. */
const noted = new Set<string>();

/** Which device this account signs in from, for the Review list on
 *  /manage/subscribers (migration 20261013000007). Fire and forget: it never
 *  delays or blocks a page, and a failure is silent. */
function noteDevice(userId: string) {
  if (noted.has(userId)) return;
  noted.add(userId);
  const id = deviceId();
  if (!id) return;
  try {
    void Promise.resolve(supabase.rpc("rb_site_note_device" as never, { p_device: id } as never)).catch(() => undefined);
  } catch { /* never let a note break a page */ }
}

export function useSiteAccess(): { access: Access; email: string | null; name: string | null; plan: SitePlan } {
  const [session, setSession] = useState<Session | undefined>(lastSession);
  const [read, setRead] = useState<Read | null>(
    lastSession && lastRead?.userId === lastSession.id ? lastRead.read : null,
  );

  useEffect(() => {
    let live = true;
    const take = (s: { user: { id: string; email?: string | null } } | null) => {
      if (!live) return;
      lastSession = s ? { id: s.user.id, email: s.user.email ?? null } : null;
      setSession(lastSession);
    };
    void supabase.auth.getSession().then(({ data }) => take(data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => take(s));
    return () => { live = false; sub.subscription.unsubscribe(); };
  }, []);

  // ⚠️ ASK AGAIN WHEN THE SCREEN COMES BACK (2026-10-13). Someone who
  // subscribes on the website in Safari and switches back to the app — or
  // who is given a free plan while the app sits in their pocket — must not
  // stay locked until they close the app. The phone app stays alive for days; a
  // page's first read is not enough there.
  const [fresh, setFresh] = useState(0);
  useEffect(() => {
    const again = () => { if (document.visibilityState === "visible") setFresh((n) => n + 1); };
    // And right after an in-app purchase or Restore (src/rbapp/appStore.ts, ACCESS_CHANGED).
    const bought = () => setFresh((n) => n + 1);
    document.addEventListener("visibilitychange", again);
    window.addEventListener("rb-access-changed", bought);
    return () => {
      document.removeEventListener("visibilitychange", again);
      window.removeEventListener("rb-access-changed", bought);
    };
  }, []);

  const userId = session?.id ?? null;
  useEffect(() => {
    if (!userId) { setRead(null); return; }
    noteDevice(userId);
    let live = true;
    // Keep the remembered answer for this person while it is re-read; clear
    // it only when the person is someone else.
    setRead(lastRead?.userId === userId ? lastRead.read : null);
    void (async () => {
      const [billing, account] = await Promise.all([
        supabase.rpc("rb_site_my_billing" as never),
        // rb_accounts is absent from the generated types; RLS returns only
        // the person's own row. The name is for a greeting only.
        (supabase as unknown as {
          from(t: "rb_accounts"): { select(c: string): { eq(c: string, v: string): { maybeSingle(): Promise<{ data: { full_name: string | null } | null }> } } };
        }).from("rb_accounts").select("full_name").eq("user_id", userId).maybeSingle(),
      ]);
      if (!live) return;
      const b = (billing.data ?? null) as {
        level?: Level; trial_ends_at?: string | null; tier?: string | null; status?: string | null;
        comp_tier?: string | null; comp_until?: string | null;
        app?: { tier: string; store: string | null; expires_at: string | null; period_type: string | null; will_renew: boolean } | null;
      } | null;
      const next: Read = {
        // A failed read is null, which decideAccess treats as open: a hiccup
        // must never wall off someone who has paid.
        level: billing.error ? null : b?.level ?? null,
        trialEndsAt: b?.trial_ends_at ?? null,
        name: account.data?.full_name?.trim() || null,
        // Any live tier row is the plan (a 'basic' row is grandfathered).
        paid: b?.tier && ["trialing", "active", "past_due"].includes(String(b.status)) ? "premium" : null,
        free: b?.comp_tier ? "premium" : null,
        freeUntil: b?.comp_until ?? null,
        app: b?.app ? { store: b.app.store, expiresAt: b.app.expires_at, trial: b.app.period_type === "trial", willRenew: b.app.will_renew !== false } : null,
      };
      lastRead = { userId, read: next };
      setRead(next);
    })();
    return () => { live = false; };
  }, [userId, fresh]);

  const loading = session === undefined || (!!userId && read === null);

  return {
    access: decideAccess({ loading, signedIn: !!userId, level: read?.level ?? null, trialEndsAt: read?.trialEndsAt ?? null }),
    email: session?.email ?? null,
    name: read?.name ?? null,
    plan: { level: read?.level ?? null, paid: read?.paid ?? null, free: read?.free ?? null, freeUntil: read?.freeUntil ?? null, app: read?.app ?? null },
  };
}
